/**
 * @fileoverview Floor Plan Sidebar Component
 * Displays unpositioned rooms and room creation options
 * @author Hospital Room Scheduler Team
 * @created 2025-10-15 23:30:00
 */

import React from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Divider,
  ScrollArea,
  Collapse,
  Box
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconTemplate,
  IconEdit
} from '@tabler/icons-react';

interface FloorPlanSidebarProps {
  floorId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onCreateFromTemplate: () => void;
  onCreateCustom: () => void;
}

/**
 * Floor Plan Sidebar - Shows unpositioned rooms and creation options
 */
export const FloorPlanSidebar: React.FC<FloorPlanSidebarProps> = ({
  floorId,
  collapsed,
  onToggleCollapse,
  onCreateFromTemplate,
  onCreateCustom
}) => {

  return (
    <Paper
      radius={0}
      style={{
        width: collapsed ? 60 : 320,
        height: '100%',
        maxHeight: '100vh',
        transition: 'width 0.2s ease',
        borderRight: '1px solid var(--mantine-color-gray-3)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Enhanced Collapse Toggle - Always visible */}
      <ActionIcon
        onClick={onToggleCollapse}
        variant="filled"
        color="blue"
        size="xl"
        style={{
          position: 'absolute',
          top: '50%',
          right: collapsed ? -24 : -24,
          transform: 'translateY(-50%)',
          zIndex: 1050,
          backgroundColor: 'var(--mantine-color-blue-6)',
          border: '3px solid white',
          boxShadow: '0 4px 16px rgba(37, 99, 235, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          width: 48,
          height: 48,
          borderRadius: '50%',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.3)';
        }}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <IconChevronRight size={20} /> : <IconChevronLeft size={20} />}
      </ActionIcon>

      {/* Collapsed State - Show minimal interface */}
      {collapsed && (
        <Stack gap="xs" p="xs" align="center" style={{ height: '100%', justifyContent: 'center' }}>
          <ActionIcon
            onClick={onCreateFromTemplate}
            variant="light"
            color="blue"
            size="lg"
            title="Create from template"
          >
            <IconTemplate size={20} />
          </ActionIcon>
          <ActionIcon
            onClick={onCreateCustom}
            variant="light"
            color="gray"
            size="lg"
            title="Create custom room"
          >
            <IconEdit size={20} />
          </ActionIcon>
        </Stack>
      )}

      <Collapse in={!collapsed}>
        <Stack gap="md" p="md" style={{ height: '100%', overflow: 'hidden' }}>
          {/* Header - Fixed */}
          <Group justify="space-between">
            <Text size="lg" fw={600}>Floor Plan Editor</Text>
          </Group>

          <Divider />

          {/* Create Room Buttons - Fixed */}
          <Box>
            <Text size="sm" fw={500} mb="sm">Create New Room</Text>
            <Stack gap="xs">
              <Button 
                leftSection={<IconTemplate size={16} />}
                onClick={onCreateFromTemplate}
                size="sm"
                fullWidth
              >
                From Template
              </Button>
              <Button 
                leftSection={<IconEdit size={16} />}
                onClick={onCreateCustom}
                size="sm"
                variant="light"
                fullWidth
              >
                Custom Room
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Instructions - Scrollable */}
          <ScrollArea style={{ flex: 1 }}>
            <Box>
              <Text size="xs" c="dimmed">
                Rooms are created and positioned automatically.
                Drag to adjust position, drag corners to resize.
              </Text>
            </Box>
          </ScrollArea>
        </Stack>
      </Collapse>
    </Paper>
  );
};

